import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { onlyUpdateForKeys } from "recompose";
import styled from "styled-components";
import styles from "./ContextualDisplay.scss";
import constants from "../../utils/constants";
import classnames from "classnames";
import { buildImgUrl, renderHTML } from "../../utils/misc";

import Thumbnail from "../Thumbnail/Thumbnail";

import { Popup, Tabs, Carousel } from "@witbooking/react-components";

import EntityResultHeader from "../EntityResultHeader/EntityResultHeader";

const DisplayContentStyled = styled.div`
  ${props => props && props.padding && "padding: 0 15px"};
  width: 100%;
  height: 100%;
`;
const SliderContainer = styled.div`
  width: 100%;
  height: ${props => (400 * props.windowWidth) / 1820 + "px"};
`;

const ContainerHasImage = styled.div`
  color: #908f96;
  font-size: 14px;
  line-height: 21.44px;
  font-weight: 400;
  text-align: left;
`;

const ContainerEntityDescription = styled.div`
  color: #908f96;
  font-size: 14px;
  line-height: 21.44px;
  font-weight: 400;
  text-align: left;
  margin-top: 10px;
  & ul {
    list-style-position: inside;
    list-style-type: disc;
  }
`;

const ContainerMap = styled.div`
  color: #908f96;
  font-size: 14px;
  line-height: 21.44px;
  font-weight: 400;
  text-align: left;

  height: calc(100% - 35px);
  width: calc(100% - 10px);

  #overMap::before {
    ${props => props && props.widthOverMap && `width: ${props.widthOverMap}px`};
    top: 50px;
  }
`;

const ContainerTabs = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ContainerThumbnail = styled.div``;

const onThumbnailClick = ({ event, handleGallery, entity }) => {
  handleGallery(entity.media, entity.name, entity.description, null, null, null, event);
};

const InfoTab = ({
  entity,
  handleGallery,
  windowWidth,
  EntityResultHeaderContainer,
  thumbnailHeight,
  thumbnailWidth,
}) => {
  if (!entity) {
    return null;
  }
  const hasImages = entity.media && entity.media.length > 0;
  return (
    <ContainerHasImage>
      {hasImages && (
        <SliderContainer
          className={classnames("wbkv9-PropertyCard-imageSlider", styles.ImageSlider)}
          windowWidth={windowWidth}>
          <Carousel style={{ height: "100%" }}>
            {entity.media &&
              entity.media.map(image => (
                <ContainerThumbnail
                  key={image.url}
                  onClick={event => onThumbnailClick({ event, handleGallery, entity })}>
                  <Thumbnail
                    src={buildImgUrl(
                      image.url,
                      thumbnailHeight !== undefined || null || false ? thumbnailHeight : 500,
                      thumbnailWidth !== undefined || null || false ? thumbnailWidth : 750,
                      "https://placehold.it/302x190"
                    )}
                    onHover={() => null}
                    title={image && image.title}
                  />
                </ContainerThumbnail>
              ))}
          </Carousel>
        </SliderContainer>
      )}
      {EntityResultHeaderContainer}
      <ContainerEntityDescription>
        <span dangerouslySetInnerHTML={renderHTML(entity.description)} />
      </ContainerEntityDescription>
    </ContainerHasImage>
  );
};

const loadContent = (container, content) => {
  const range = document.createRange();
  range.selectNode(container.current);
  const documentFragment = range.createContextualFragment(content);
  container.current.innerHTML = "";
  container.current.appendChild(documentFragment);
};

const CustomTab = props => {
  const container = useRef(null);

  useEffect(() => {
    if (container.current) {
      loadContent(container, props.element.content);
    }
  }, [props.element.content]);

  const { element } = props;
  if (!element) {
    return null;
  }

  return (
    <DisplayContentStyled padding={props.padding}>
      <div className={classnames("wbkv9-DisplayContent", styles.DisplayContent)} ref={container} />
    </DisplayContentStyled>
  );
};

const getContent = ({
  element,
  mapContent,
  onOpenCalendar,
  activeEntity,
  padding,
  windowWidth,
  EntityResultHeaderContainer,
  handleGallery,
  containerTab,
}) => {
  switch (element.type) {
    case constants.DISPLAY_ELEMENT_TYPE.INFO:
      return (
        <InfoTab
          entity={activeEntity}
          handleGallery={handleGallery}
          windowWidth={windowWidth}
          EntityResultHeaderContainer={EntityResultHeaderContainer}
        />
      );
    case constants.DISPLAY_ELEMENT_TYPE.MAP:
      return (
        <ContainerMap widthOverMap={containerTab?.current?.offsetWidth || 200}>
          {mapContent}
        </ContainerMap>
      );
    case constants.DISPLAY_ELEMENT_TYPE.AVAILABILITY:
      onOpenCalendar({ propertyTicker: activeEntity.ticker });
      return null;
    case constants.DISPLAY_ELEMENT_TYPE.CUSTOM:
      if (Array.isArray(element.multimedia) && element.multimedia.length) {
        return (
          <InfoTab
            entity={{ media: element.multimedia, description: element.content }}
            handleGallery={handleGallery}
            windowWidth={windowWidth}
            thumbnailHeight={0}
            thumbnailWidth={768}
            EntityResultHeaderContainer={
              <EntityResultHeader entity={{ name: element.label }} nature={"large"} />
            }
          />
        );
      }
      return <CustomTab element={element} padding={padding} />;
    default:
      return "";
  }
};

const ContextualDisplay = props => {
  const [state, setState] = useState({ displayElements: null });
  const {
    activeDisplayElement,
    onSelectElement,
    mapContent,
    activeEntity,
    isPropertyChain,
    handleGallery,
    onOpenCalendar,
    handleModalContent,
    windowWidth,
    EntityResultHeaderContainer,
    renderAsModal,
    BackArrow,
    handleCloseModal,
    isActiveEntityChain,
  } = props;

  const containerTab = useRef(null);

  const { displayElements } = state;

  const setFirstDefaultOption = displayElements => {
    const elementsDefaultOption = [...displayElements.filter(e => e.defaultOption)];
    const elementsNoDefaultOption = [...displayElements.filter(e => !e.defaultOption)];
    return [...elementsDefaultOption, ...elementsNoDefaultOption];
  };

  useEffect(() => {
    if (props?.displayElements?.length) {
      const displayElements = setFirstDefaultOption(props.displayElements);
      setState({ displayElements });
    }
  }, [props.displayElements]);

  if (!activeDisplayElement || !displayElements) {
    return null;
  }

  if (
    renderAsModal &&
    activeDisplayElement.type !== constants.DISPLAY_ELEMENT_TYPE.AVAILABILITY &&
    activeDisplayElement.type !== constants.DISPLAY_ELEMENT_TYPE.MAP
  ) {
    handleModalContent(
      <Popup closeWindow={handleCloseModal}>
        {getContent({
          element: activeDisplayElement,
          mapContent,
          activeEntity,
          isPropertyChain,
          handleGallery,
          windowWidth,
          padding: true,
          EntityResultHeaderContainer: EntityResultHeaderContainer,
          onOpenCalendar,
        })}
      </Popup>,
      {
        hideCloseButton: true,
        shouldCloseOnEsc: false,
      }
    );

    return null;
  }

  return (
    <ContainerTabs ref={containerTab}>
      <Tabs
        activeEntityChain={!isActiveEntityChain}
        displayElements={displayElements}
        onSelectElement={onSelectElement}
        defItemTickerSelected={"" + activeDisplayElement.ticker}
        BackArrow={BackArrow}
        children={getContent({
          element: activeDisplayElement,
          mapContent,
          EntityResultHeaderContainer,
          windowWidth,
          activeEntity,
          isPropertyChain,
          handleGallery,
          onOpenCalendar,
          containerTab,
        })}
      />
    </ContainerTabs>
  );
};

ContextualDisplay.propTypes = {
  displayElements: PropTypes.array,
  activeDisplayElement: PropTypes.object,
  onSelectElement: PropTypes.func.isRequired,
  activeEntity: PropTypes.object,
  mapContent: PropTypes.object,
  isPropertyChain: PropTypes.bool,
  handleGallery: PropTypes.func.isRequired,
  onOpenCalendar: PropTypes.func.isRequired,
  BackArrow: PropTypes.object.isRequired,
  handleModalContent: PropTypes.func,
  handleCloseModal: PropTypes.func,
  windowWidth: PropTypes.number.isRequired,
  EntityResultHeaderContainer: PropTypes.any,
  renderAsModal: PropTypes.bool,
};

ContextualDisplay.defaultProps = {
  displayElements: [
    {
      ticker: "info",
      type: "INFO",
      label: "Info",
      content: null,
      conditions: {
        ticker: {
          type: "ALL",
          value: null,
        },
        entity: {
          type: "ALL",
          value: null,
        },
      },
    },
    {
      ticker: "map",
      type: "MAP",
      label: "Map",
      entity: {
        type: "ALL",
        value: ["PROPERTY"],
      },
    },
    {
      ticker: "availability",
      type: "AVAILABILITY",
      label: "Availability",
      content: null,
      icon: "availability",
      conditions: {
        entity: {
          type: "ALL",
          value: ["PROPERTY"],
        },
      },
    },
    {
      ticker: "JnrSuite360View",
      type: "CUSTOM",
      label: "Custom",
      content: null,
      conditions: {
        ticker: {
          type: "ALL",
          value: ["jnr"],
        },
        entity: {
          type: "ALL",
          value: ["ACCOMMODATION"],
        },
      },
    },
  ],
  activeDisplayElement: {
    ticker: "info",
    type: constants.DISPLAY_ELEMENT_TYPE.MAP,
    label: "Info",
    content: null,
    conditions: {
      ticker: {
        type: "ALL",
        value: null,
      },
      entity: {
        type: "ALL",
        value: null,
      },
    },
  },
  activeEntity: null,
  mapContent: null,
};

const conditionalUpdate = onlyUpdateForKeys([
  "hotelSelected",
  "displayElements",
  "activeDisplayElement",
  "onSelectElement",
  "windowWidth",
  "activeEntity",
]);

export default conditionalUpdate(ContextualDisplay);
